import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { SubHubGrid, ContentSection, FaqBlock, buildFaqLd, buildBreadcrumbLd, ResultCard, ReservedResultsArea, inputCls } from "@/components/age-calculator/shared";
import { TripleDateInput, AnalyzeButton, AnalyzingOverlay, WhatsAppShareButton, useAnalyzeGate } from "@/components/age-calculator/premium";

const TITLE = "Chinese Lunar Age & Face/Skin Aging Calculator Online";
const DESC = "Discover your traditional Chinese lunar calendar age or estimate physical skin/facial maturation trends based on environmental factors.";

const ZODIAC = ["Monkey", "Rooster", "Dog", "Pig", "Rat", "Ox", "Tiger", "Rabbit", "Dragon", "Snake", "Horse", "Goat"];
const ZODIAC_MAP: Record<string, { emoji: string; traits: string; element: string }> = {
  Rat:     { emoji: "🐀", traits: "Quick-witted, resourceful, versatile",     element: "Water" },
  Ox:      { emoji: "🐂", traits: "Diligent, dependable, strong",              element: "Earth" },
  Tiger:   { emoji: "🐅", traits: "Brave, confident, competitive",             element: "Wood" },
  Rabbit:  { emoji: "🐇", traits: "Gentle, elegant, kind",                     element: "Wood" },
  Dragon:  { emoji: "🐉", traits: "Charismatic, ambitious, energetic",         element: "Earth" },
  Snake:   { emoji: "🐍", traits: "Wise, mysterious, intuitive",               element: "Fire" },
  Horse:   { emoji: "🐎", traits: "Free-spirited, animated, warm-hearted",     element: "Fire" },
  Goat:    { emoji: "🐐", traits: "Calm, gentle, sympathetic",                 element: "Earth" },
  Monkey:  { emoji: "🐒", traits: "Sharp, curious, inventive",                 element: "Metal" },
  Rooster: { emoji: "🐓", traits: "Observant, hardworking, courageous",         element: "Metal" },
  Dog:     { emoji: "🐕", traits: "Loyal, honest, responsible",                 element: "Earth" },
  Pig:     { emoji: "🐖", traits: "Compassionate, generous, diligent",          element: "Water" },
};

const FAQS = [
  { q: "How is Chinese lunar age different from Western age?", a: "In East Asian tradition, a baby is considered 1 year old at birth and gains a year every Lunar New Year — not on their birthday. That means lunar age is typically 1–2 years higher than Western chronological age." },
  { q: "How accurate is a skin-age estimate?", a: "Skin biological age depends on genetics, UV exposure, hydration, sleep and smoking. Our estimate is an educational guide — for clinical measurement dermatologists use corneometry or reflectance spectroscopy." },
  { q: "What determines my Chinese zodiac animal?", a: "The Chinese zodiac cycles every 12 years starting from the Rat. The animal is assigned by lunar-year of birth; babies born in January or early February may fall under the previous year's animal." },
  { q: "Can lifestyle really change how fast my skin ages?", a: "Yes — up to 80% of visible facial aging is caused by cumulative UV exposure. Daily SPF, no smoking, adequate sleep and hydration slow visible aging more than any topical product." },
];

export const Route = createFileRoute("/age-calculator/lifestyle-lunar-face")({
  head: () => leafHead({ path: "/age-calculator/lifestyle-lunar-face", title: TITLE, description: DESC, scripts: [buildFaqLd(FAQS), buildBreadcrumbLd([{ name: "Home", url: "https://drhint.com/" }, { name: "Age Calculator", url: "https://drhint.com/age-calculator" }, { name: "Lunar & Alternative", url: "https://drhint.com/age-calculator/lifestyle-lunar-face" }])] }),
  component: Page,
});

function Page() {
  const [dob, setDob] = useState("");
  const [sun, setSun] = useState<"low" | "medium" | "high">("medium");
  const [smoker, setSmoker] = useState(false);
  const { analyzing, revealed, trigger, reset } = useAnalyzeGate();
  const canGo = !!dob;

  const r = useMemo(() => {
    if (!dob) return null;
    const b = new Date(dob);
    if (isNaN(b.getTime())) return null;
    const now = new Date();
    const chrono = Math.floor((now.getTime() - b.getTime()) / (365.25 * 86400000));
    // Traditional East-Asian counting: age 1 at birth + 1 for each Lunar New Year passed.
    const lunarNewYearsPassed = now.getFullYear() - b.getFullYear();
    const lunarAge = chrono + (now.getMonth() >= 1 ? 1 : 0) + (chrono === 0 ? 1 : 0) || lunarNewYearsPassed + 1;
    const zodiac = ZODIAC[b.getFullYear() % 12];
    const zInfo = ZODIAC_MAP[zodiac];
    let skin = chrono;
    if (sun === "high") skin += 5; else if (sun === "medium") skin += 2;
    if (smoker) skin += 4;
    const timeline = [
      { age: 20, note: "Peak collagen" },
      { age: 30, note: "First fine lines begin" },
      { age: 40, note: "Volume loss around cheeks" },
      { age: 50, note: "Deeper wrinkles, elasticity drop" },
      { age: 60, note: "Pigment changes, thinner skin" },
    ];
    return { chrono, lunarAge, zodiac, zInfo, skin, timeline };
  }, [dob, sun, smoker]);

  const shareMsg = r ? `🌙 My Lunar Age Profile\n\n📅 Chronological: ${r.chrono} yrs\n🏮 Chinese lunar age: ${r.lunarAge} 岁\n${r.zInfo.emoji} Zodiac: ${r.zodiac} (${r.zInfo.element})\n\nCheck yours on https://drhint.com/age-calculator/lifestyle-lunar-face` : "";

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/age-calculator" className="hover:text-primary">Age Calculator</Link> / Lifestyle, Lunar & Face
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{TITLE}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">{DESC}</p>
      </section>
      <section className="mx-auto max-w-5xl px-4">
        <div className="rounded-3xl border border-border bg-gradient-to-br from-indigo-50/40 via-card to-purple-50/40 dark:from-indigo-950/20 dark:to-purple-950/20 p-6 shadow-sm">
          <div className="grid gap-5 sm:grid-cols-3">
            <TripleDateInput label="Date of Birth" value={dob} onChange={(v) => { setDob(v); reset(); }} />
            <label className="flex flex-col gap-2" style={{ minHeight: 92 }}>
              <span className="text-sm font-semibold">Daily Sun Exposure</span>
              <select value={sun} onChange={(e) => { setSun(e.target.value as typeof sun); reset(); }} className={inputCls()}>
                <option value="low">Low (indoors, SPF daily)</option>
                <option value="medium">Medium (regular outdoor time)</option>
                <option value="high">High (outdoor job / no SPF)</option>
              </select>
            </label>
            <label className="flex items-end gap-2 pb-2" style={{ minHeight: 92 }}>
              <input type="checkbox" checked={smoker} onChange={(e) => { setSmoker(e.target.checked); reset(); }} />
              <span className="text-sm font-semibold">Current smoker</span>
            </label>
          </div>
          <AnalyzeButton onClick={trigger} disabled={!canGo || analyzing} label="Reveal Lunar Profile" />
          <AnalyzingOverlay visible={analyzing} />
          <ReservedResultsArea minHeight={300}>
            {revealed && r ? (
              <>
                <div className="rounded-2xl border border-indigo-200/50 dark:border-indigo-800/50 bg-gradient-to-br from-indigo-500/10 via-card to-purple-500/10 p-6 flex items-center gap-5" style={{ minHeight: 140 }}>
                  <div className="text-6xl">{r.zInfo.emoji}</div>
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Chinese Zodiac</div>
                    <div className="text-3xl font-black text-indigo-600 dark:text-indigo-300">{r.zodiac}</div>
                    <div className="text-sm text-muted-foreground mt-1">Element: <span className="font-semibold text-foreground">{r.zInfo.element}</span></div>
                    <div className="text-sm mt-1">{r.zInfo.traits}</div>
                  </div>
                </div>
                <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <ResultCard label="Chronological Age" value={`${r.chrono} yrs`} />
                  <ResultCard label="Chinese Lunar Age" value={`${r.lunarAge} 岁`} sub="Traditional East-Asian counting" />
                  <ResultCard label="Zodiac Animal" value={r.zodiac} />
                  <ResultCard label="Estimated Skin Age" value={`${r.skin} yrs`} sub={`${r.skin - r.chrono >= 0 ? "+" : ""}${r.skin - r.chrono} vs actual`} />
                </div>
                <div className="mt-6">
                  <div className="text-sm font-semibold mb-2">Skin maturation timeline</div>
                  <div className="rounded-xl border border-border overflow-hidden">
                    {r.timeline.map((t) => (
                      <div key={t.age} className={`flex items-center gap-4 p-3 border-b border-border last:border-0 ${r.chrono >= t.age ? "bg-primary/5" : ""}`}>
                        <div className="w-14 font-bold text-primary tabular-nums">{t.age}</div>
                        <div className="text-sm">{t.note}</div>
                        {r.chrono >= t.age && <span className="ml-auto text-xs text-primary">✓ reached</span>}
                      </div>
                    ))}
                  </div>
                </div>
                <WhatsAppShareButton message={shareMsg} label="Share My Lunar Age" />
              </>
            ) : !analyzing ? (
              <div className="mt-6 rounded-2xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground">Enter your date of birth and tap Reveal.</div>
            ) : null}
          </ReservedResultsArea>
        </div>
      </section>
      <ContentSection title="Lunar age, zodiac and the science of visible aging">
        <p>
          In China, Korea and much of East Asia, a person is <strong>one year old at birth</strong> and adds a year every
          Lunar New Year rather than on their solar birthday. So a baby born in December can be "2 years old" a few weeks
          later. This calculator gives the traditional count alongside your Western chronological age.
        </p>
        <p>
          Your <strong>zodiac animal</strong> comes from the 12-year cycle (Rat, Ox, Tiger, Rabbit, Dragon, Snake, Horse,
          Goat, Monkey, Rooster, Dog, Pig). Each animal is paired with one of five elements (Wood, Fire, Earth, Metal,
          Water) — the combination repeats every 60 years, which is why 60 is a milestone birthday in East Asia.
        </p>
        <h3>Face and skin aging is mostly environmental</h3>
        <p>
          Dermatology research consistently shows that around <strong>80% of visible facial aging</strong> is caused by
          UV exposure, smoking and sleep debt — not intrinsic genetics. The skin-age card above applies conservative
          adjustments for daily sun exposure and smoking, mirroring the same variables used in dermatology risk models.
        </p>
        <p>
          For a numerical, cardiovascular view of aging, cross-check with the{" "}
          <Link to="/age-calculator/biological-health-bmi" className="text-primary underline">Biological & Health Age tool</Link>.
          Or return to the <Link to="/age-calculator" className="text-primary underline">main Age Calculator hub</Link> for
          the full toolset.
        </p>
      </ContentSection>
      <FaqBlock faqs={FAQS} />
      <SubHubGrid excludePath="/age-calculator/lifestyle-lunar-face" />
    </Layout>
  );
}